import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import classNames from 'classnames';
import {
  ArrowDownIcon,
  Badge,
  CautionGroup,
  DecryptIcon,
  EmptyListMobileIcon,
  EncMailIcon,
  ExchangeFullIcon,
  ExchangeOfflineIcon,
  InfoCard,
  PcTakeOutIcon,
  PrintFileIcon,
  PrintPeriodIcon,
  PrintPolicyIcon,
  ScanExemptIcon,
  ScanLedgerIcon,
  SecurityScreenIcon,
  TempRightIcon,
  useAlert,
} from 'fasoo-ui-component-next';
import moment from 'moment';

import approvalDetailApi, {
  ApprovalDetailEntity,
  ApprovalDoneDetailReqDto,
  ApprovalStatusType,
  ApprovalType,
  defaultApprovalDoneDetailReqDto,
} from 'src/api/user/approvalApi';
import decryptApprovalApi, { DecryptApprovalDetailEntity } from 'src/api/user/decryptApprovalApi';
import encMailApprovalApi, { EncMailApprovalDetailEntity } from 'src/api/user/encMailApprovalApi';
import exchangeFullApprovalApi, {
  ExchangeFullApprovalDetailEntity,
} from 'src/api/user/exchangeFullApprovalApi';
import exchangeOfflineApprovalApi, {
  ExchangeOfflineApprovalDetailEntity,
} from 'src/api/user/exchangeOfflineApprovalApi';
import printClientApprovalApi, {
  PrintClientApprovalDetailEntity,
} from 'src/api/user/printClientApprovalApi';
import printFileApprovalApi, {
  PrintFileApprovalDetailEntity,
} from 'src/api/user/printFileApprovalApi';
import printPeriodApprovalApi, {
  PrintPeriodApprovalDetailEntity,
} from 'src/api/user/printPeriodApprovalApi';
import provisionalApprovalApi, {
  ProvisionalApprovalDetailEtity,
} from 'src/api/user/provisionalApprovalApi';
import scanExemptApprovalApi, {
  ScanExemptApprovalDetailEntity,
} from 'src/api/user/scanExemptApprovalApi';
import scanLedgerApprovalApi, {
  ScanLedgerApprovalDetailEntity,
} from 'src/api/user/scanLedgerApprovalApi';
import screenApprovalApi, { ScreenApprovalDetailEntity } from 'src/api/user/screenApprovalApi';
import takeOutApprovalApi, { TakeOutApprovalDetailEntity } from 'src/api/user/takeOutApprovalApi';
import { PageProps } from 'src/components/helpers/props';
import ApprovalDetailInfo from 'src/components/molecules/ApprovalDetailInfo';
import useIntersectionObserver from 'src/hooks/useIntersectionObserverTarget';
import useProgressStore from 'src/hooks/useProgressStore';
import { getFasooUiComponentLanguage } from 'src/i18n';
import ContentLayout from 'src/layout/Content';
import ContentBody from 'src/layout/Content/ContentBody';
import ContentHeader, { ContentHeaderTab } from 'src/layout/Content/ContentHeader';
import config from 'src/shared/config';

import './index.scss';

const defaultApprovalStatuses: string[] = [
  ApprovalStatusType.PROGRESS,
  ApprovalStatusType.APPROVAL,
  ApprovalStatusType.REJECT,
];

const loadData = async (
  reqDto: ApprovalDoneDetailReqDto,
  offset: number,
  limit: number
): Promise<{
  list: ApprovalDetailEntity[];
}> => {
  let nxtReaDto = reqDto;
  if (
    reqDto.approvalStatuses === undefined ||
    reqDto.approvalStatuses === null ||
    reqDto.approvalStatuses.length < 1
  ) {
    nxtReaDto = { ...reqDto, approvalStatuses: defaultApprovalStatuses };
  }
  const list = await approvalDetailApi.donelist(nxtReaDto, offset, limit);
  // console.log(list.length);
  if (list !== null && list !== undefined && list.length > 0) {
    return { list };
  }
  return { list: [] };
};

export const findApprovalTypeIcon = (type: string): React.JSX.Element => {
  if (type === 'TAKEOUT') {
    return <PcTakeOutIcon size="lg" />;
  }
  if (type === 'DECRYPT') {
    return <DecryptIcon size="lg" />;
  }
  if (type === 'PROVISIONAL') {
    return <TempRightIcon size="lg" />;
  }
  if (type === 'SCREEN') {
    return <SecurityScreenIcon size="lg" />;
  }
  if (type === 'PRINTPOLICY') {
    return <PrintPeriodIcon size="lg" />;
  }
  if (type === 'PRINTFILE') {
    return <PrintFileIcon size="lg" />;
  }
  if (type === 'PRINTCLIENT') {
    return <PrintPolicyIcon size="lg" />;
  }
  if (type === 'SCANEXEMPT') {
    return <ScanExemptIcon size="lg" />;
  }
  if (type === 'SCANLEDGER') {
    return <ScanLedgerIcon size="lg" />;
  }
  if (type === 'ENCMAIL') {
    return <EncMailIcon size="lg" />;
  }
  if (type === 'EXCHANGEFULL') {
    return <ExchangeFullIcon size="lg" />;
  }
  if (type === 'EXCHANGEOFFLINE') {
    return <ExchangeOfflineIcon size="lg" />;
  }
  return <span style={{ padding: '20px' }} />;
};

const ApprovalCompleteList: React.FC<PageProps> = ({ routeTable }) => {
  const { alert } = useAlert();

  const { t } = useTranslation(['menu', 'commonsMenu', 'userApproval', 'userRequest', 'encMail']);
  const [offset, setOffset] = useState<number>(0);
  const [limit] = useState<number>(30);
  const [completeItemList, setCompleteItemList] = useState<ApprovalDetailEntity[]>([]);
  const [reqDto] = useState<ApprovalDoneDetailReqDto>(defaultApprovalDoneDetailReqDto());
  const [loading, setLoading] = useState<boolean>(false);
  const [isEnter, setIsEnter] = useState<boolean>(false);
  const { startProgress, stopProgress, inProgress } = useProgressStore();

  const navigate = useNavigate();
  const locationVal = useLocation();

  const queryParams = new URLSearchParams(locationVal.search);
  const urlMailPreview = queryParams.get('mailPreview');

  const getCompleteItemLists = async (): Promise<void> => {
    setLoading(true);
    startProgress();
    setTimeout(async () => {
      try {
        const { list } = await loadData(reqDto, offset, limit);
        // console.log('complete list len: ', list.length);
        if (list.length > 0) {
          setCompleteItemList([...completeItemList, ...list]);
        }
        stopProgress();
        if (list.length >= limit) {
          setLoading(false);
        }
      } catch (error) {
        stopProgress();
        setLoading(true);
      }
    }, 500);

    setOffset(offset + limit);
  };

  useEffect(() => {
    if (isEnter === false) {
      getCompleteItemLists();
      setIsEnter(true);
    }
  }, []);

  // eslint-disable-next-line no-undef
  const onIntersect: IntersectionObserverCallback = ([{ isIntersecting }]) => {
    if (isIntersecting === true && !loading) {
      getCompleteItemLists();
    }
  };
  const { target } = useIntersectionObserver<HTMLDivElement>({ onIntersect });
  // const {
  //   isComponentVisible: detailsOpen,
  //   setIsComponentVisible: setDetailsOpen,
  //   ref,
  // } = useComponentVisible(false);
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);

  const forcedUpdateDetailData = (): void => {
    console.log('reset');
  };

  const [selectedApproval, setSelectedApproval] = useState<ApprovalDetailEntity | null>(null);
  const [takeOutDetailData, setTakeOutDetailData] = useState<TakeOutApprovalDetailEntity | null>(
    null
  );
  const [screenDetailData, setScreenDetailData] = useState<ScreenApprovalDetailEntity | null>(null);
  const [decryptDetailData, setDecryptDetailData] = useState<DecryptApprovalDetailEntity | null>(
    null
  );
  const [provisionalDetailData, setProvisionalDetailData] =
    useState<ProvisionalApprovalDetailEtity | null>(null);

  // v2.1 새로 추가된 정책
  const [printPeriodDetailData, setPrintPeriodDetailData] =
    useState<PrintPeriodApprovalDetailEntity | null>(null);
  const [printFileDetailData, setPrintFileDetailData] =
    useState<PrintFileApprovalDetailEntity | null>(null);
  const [printClientDetailData, setPrintClientDetailData] =
    useState<PrintClientApprovalDetailEntity | null>(null);

  const [scanExemptDetailData, setScanExemptDetailData] =
    useState<ScanExemptApprovalDetailEntity | null>(null);
  const [scanLedgerDetailData, setScanLedgerDetailData] =
    useState<ScanLedgerApprovalDetailEntity | null>(null);

  // v2.3 새로 추가된 암호화 해제(메일 반출)
  const [encMailDetailData, setEncMailDetailData] = useState<EncMailApprovalDetailEntity | null>(
    null
  );

  const [exchangeFullDetailData, setExchangeFullDetailData] =
    useState<ExchangeFullApprovalDetailEntity | null>(null);
  const [exchangeOfflineDetailData, setExchangeOfflineDetailData] =
    useState<ExchangeOfflineApprovalDetailEntity | null>(null);

  useEffect(() => {
    if (selectedApproval !== null && locationVal.search.length === 0) {
      setDetailsOpen(false);
      setSelectedApproval(null);
      setDecryptDetailData(null);
      setTakeOutDetailData(null);
      setScreenDetailData(null);
      setProvisionalDetailData(null);
      setPrintPeriodDetailData(null);
      setPrintFileDetailData(null);
      setPrintClientDetailData(null);
      setScanExemptDetailData(null);
      setScanLedgerDetailData(null);
      setEncMailDetailData(null);
    } else if (selectedApproval === null && locationVal.search.includes('approvalNo')) {
      navigate(config.url(`/approval/complete`));
    }
  }, [locationVal.search]);

  const handleDetailOpen = (open: boolean): void => {
    if (!open && urlMailPreview) {
      navigate(-1);
      return;
    }
    setDetailsOpen(open);
    if (!open) {
      setSelectedApproval(null);
      setDecryptDetailData(null);
      setTakeOutDetailData(null);
      setScreenDetailData(null);
      setProvisionalDetailData(null);
      setPrintPeriodDetailData(null);
      setPrintFileDetailData(null);
      setPrintClientDetailData(null);
      setScanExemptDetailData(null);
      setScanLedgerDetailData(null);
      setEncMailDetailData(null);
      setExchangeFullDetailData(null);
      setExchangeOfflineDetailData(null);
      navigate(-1);
    }
  };

  const handleChangeUrl = (approvalNo: string): void => {
    if (locationVal.search.length > 0) {
      navigate(-1);
    } else {
      navigate(config.url(`/approval/complete?approvalNo=${approvalNo}`));
    }
  };
  const handleMoveBackUrl = (): void => {
    if (locationVal.search.length > 0) {
      handleDetailOpen(false);
    }
  };

  const handleSelectedApproval = async (entity: ApprovalDetailEntity): Promise<void> => {
    setSelectedApproval(entity);
    handleDetailOpen(true);
    if (entity.approvalType === ApprovalType.TAKEOUT) {
      await takeOutApprovalApi
        .find(entity.approvalNo)
        .then((resp) => setTakeOutDetailData(resp))
        .catch((error) => alert(error, 'warning'));
    } else if (entity.approvalType === ApprovalType.SCREEN) {
      await screenApprovalApi
        .find(entity.approvalNo)
        .then((resp) => setScreenDetailData(resp))
        .catch((error) => alert(error, 'warning'));
    } else if (entity.approvalType === ApprovalType.DECRYPT) {
      // setDecryptApprovalNo(entity.approvalNo);
      await decryptApprovalApi
        .find(entity.approvalNo)
        .then((resp) => setDecryptDetailData(resp))
        .catch((error) => alert(error, 'warning'));
    } else if (entity.approvalType === ApprovalType.PROVISIONAL) {
      await provisionalApprovalApi
        .find(entity.approvalNo)
        .then((resp) => setProvisionalDetailData(resp))
        .catch((error) => alert(error, 'warning'));
    } else if (entity.approvalType === ApprovalType.PRINTPOLICY) {
      await printPeriodApprovalApi
        .find(entity.approvalNo)
        .then((resp) => setPrintPeriodDetailData(resp))
        .catch((error) => alert(error, 'warning'));
    } else if (entity.approvalType === ApprovalType.PRINTFILE) {
      await printFileApprovalApi
        .find(entity.approvalNo)
        .then((resp) => setPrintFileDetailData(resp))
        .catch((error) => alert(error, 'warning'));
    } else if (entity.approvalType === ApprovalType.PRINTCLIENT) {
      await printClientApprovalApi
        .find(entity.approvalNo)
        .then((resp) => setPrintClientDetailData(resp))
        .catch((error) => alert(error, 'warning'));
    } else if (entity.approvalType === ApprovalType.SCANEXEMPT) {
      await scanExemptApprovalApi
        .find(entity.approvalNo)
        .then((resp) => setScanExemptDetailData(resp))
        .catch((error) => alert(error, 'warning'));
    } else if (entity.approvalType === ApprovalType.SCANLEDGER) {
      await scanLedgerApprovalApi
        .find(entity.approvalNo)
        .then((resp) => setScanLedgerDetailData(resp))
        .catch((error) => alert(error, 'warning'));
    } else if (entity.approvalType === ApprovalType.ENCMAIL) {
      await encMailApprovalApi
        .find(entity.approvalNo)
        .then((resp) => setEncMailDetailData(resp))
        .catch((error) => alert(error, 'warning'));
    } else if (entity.approvalType === ApprovalType.EXCHANGEFULL) {
      await exchangeFullApprovalApi
        .find(entity.approvalNo)
        .then((resp) => setExchangeFullDetailData(resp))
        .catch((error) => alert(error, 'warning'));
    } else if (entity.approvalType === ApprovalType.EXCHANGEOFFLINE) {
      await exchangeOfflineApprovalApi
        .find(entity.approvalNo)
        .then((resp) => setExchangeOfflineDetailData(resp))
        .catch((error) => alert(error, 'warning'));
    }
    handleChangeUrl(entity.approvalNo);
  };

  // 위로 이동
  const handleGoTop = (): void => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  };
  const [isScorolled, setIsScrolled] = useState<boolean>(false);
  const checkScrolled = useCallback(() => {
    if (window.scrollY > 60) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', checkScrolled);
    return () => {
      window.removeEventListener('scroll', checkScrolled);
    };
  }, []);

  return (
    <ContentLayout>
      <ContentHeader>
        <ContentHeaderTab routeTable={routeTable} />
      </ContentHeader>
      {/* content 표시 */}
      <ContentBody>
        <div className="user-approval-donelist-root">
          <div className="item-list-container">
            {completeItemList.map((item, itemIdx) => {
              return (
                <div
                  className="item-list-box"
                  // eslint-disable-next-line react/no-array-index-key
                  key={item.approvalNo + itemIdx}
                  style={{
                    width: `100%`,
                    height: `80px`,
                    boxSizing: 'border-box',
                  }}
                  onClick={() => handleSelectedApproval(item)}
                >
                  <InfoCard
                    startArea={findApprovalTypeIcon(item.approvalType)}
                    title={`${item.userName} (${item.userId})`}
                    description={item.remark}
                    size="md"
                    endArea={
                      <div className="end-area-info__root">
                        <div className="end-area-info__date">
                          {getFasooUiComponentLanguage() === 'en'
                            ? moment(item.updatedTs).format('MM/DD/YYYY').toString()
                            : moment(item.updatedTs).format('YYYY-MM-DD').toString()}
                          <br />
                          {getFasooUiComponentLanguage() === 'en'
                            ? moment(item.updatedTs).format('A h:mm').toString()
                            : moment(item.updatedTs).locale('ko').format('A h:mm').toString()}
                        </div>
                        <div className="end-area-info__status">
                          <Badge
                            color="gray"
                            text={t(`userApproval:status.${item.status}`)}
                            ellipse
                            classes={classNames({
                              'badge-color__done': item.status === 'APPROVAL',
                              'badge-color__progress': item.status === 'PROGRESS',
                              'badge-color__reject': item.status === 'REJECT',
                              'badge-color__not-applied': item.status === 'CANCEL',
                            })}
                          />
                        </div>
                      </div>
                    }
                    ellipsisContent
                    descriptionMultiline={2}
                  />
                </div>
              );
            })}
            {!inProgress && loading && completeItemList.length === 0 && (
              <CautionGroup
                title={t('userApproval:message.noData.$')}
                description={t('userApproval:message.noData.complete')}
                classes="caution-empty"
                icon={<EmptyListMobileIcon />}
              />
            )}
            <div
              style={{
                height: '10px',
                minHeight: `10px`,
                boxSizing: 'border-box',
              }}
              ref={target}
            />
          </div>

          {detailsOpen && (
            <div>
              <div onClick={handleMoveBackUrl} className="detail-info-open" />
              <div className="detail-info">
                <ApprovalDetailInfo
                  open={detailsOpen}
                  selectItem={selectedApproval !== null}
                  type={`${selectedApproval?.approvalType.toLowerCase()}Approval`}
                  ResetDetail={() => forcedUpdateDetailData()}
                  screenApprovalDetailInfo={screenDetailData}
                  takeoutApprovalDetailInfo={takeOutDetailData}
                  decryptApprovalDetailInfo={decryptDetailData}
                  provisionalApprovalDetailInfo={provisionalDetailData}
                  printperiodpolicyApprovalDetailInfo={printPeriodDetailData}
                  printFileApprovalDetailInfo={printFileDetailData}
                  printClientApprovalDetailInfo={printClientDetailData}
                  scanExemptApprovalDetailInfo={scanExemptDetailData}
                  scanLedgerApprovalDetailInfo={scanLedgerDetailData}
                  encMailApprovalDetailInfo={encMailDetailData}
                  exchangeFullApprovalDetailInfo={exchangeFullDetailData}
                  exchangeOfflineApprovalDetailInfo={exchangeOfflineDetailData}
                  onCloseDetail={handleDetailOpen}
                  isComplete
                  selectedApproval={selectedApproval}
                />
              </div>
            </div>
          )}
          <div
            className={classNames('goto-top', { on: isScorolled })}
            onClick={() => handleGoTop()}
          >
            <ArrowDownIcon classes="rotate-180" />
          </div>
        </div>
      </ContentBody>
    </ContentLayout>
  );
};

export default ApprovalCompleteList;
